import React, { useState, useEffect } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { 
    Box, Typography, Button, Paper, Container, CircularProgress, 
    Alert, Stepper, Step, StepLabel, Divider, Stack 
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import SecurityIcon from '@mui/icons-material/Security';
import CreditCardIcon from '@mui/icons-material/CreditCard';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

const THEME_COLORS = {
    primary: '#11224d',
    secondary: '#0e1d3a',
    warning: '#ffb300',
    background: '#f8fafc'
};

const PLAN_NAMES = {
    broker: 'ShipperDock Broker Access',
    carrier: 'ShipperDock Carrier Access',
    shipper: 'ShipperDock Shipper Access'
};

const CheckoutForm = ({ clientSecret, isTrial, requiresPaymentMethod, priceId, trialDays }) => {
    const stripe = useStripe();
    const elements = useElements();
    const [message, setMessage] = useState(null);
    const [isLoading, setIsLoading] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!stripe || !elements) return;

        setIsLoading(true);
        const { error } = await stripe.confirmPayment({
            elements,
            confirmParams: {
                return_url: `${window.location.origin}/dashboard?subscription_status=active`,
            },
        });

        if (error) {
            setMessage(error.message);
            setIsLoading(false);
        }
    };

    const handleSetup = async (e) => {
        e.preventDefault();
        if (!stripe || !elements) return;
        setIsLoading(true);

        if (requiresPaymentMethod) {
            
            const result = await stripe.confirmSetup({
                elements,
                confirmParams: {
                    return_url: `${window.location.origin}/dashboard`,
                },
                redirect: 'if_required',
            });

            if (result.error) {
                setMessage(result.error.message);
                setIsLoading(false);
            } else {
                
                try {
                    const token = localStorage.getItem('jwtToken');
                    const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:5000';
                    
                    const res = await axios.post(
                        `${API_BASE}/api/stripe/create-subscription`,
                        { 
                            priceId: priceId,
                            paymentMethodId: result.setupIntent.payment_method
                        },
                        { headers: { Authorization: `Bearer ${token}` } }
                    );

                    if (res.data.status === 'active' || res.data.status === 'trialing') {
                        window.location.href = '/dashboard?subscription_status=active';
                    } else {
                        setMessage("Subscription status: " + res.data.status);
                        setIsLoading(false);
                    }
                } catch (err) {
                    console.error(err);
                    setMessage(err.response?.data?.message || "Failed to activate subscription.");
                    setIsLoading(false);
                }
            }
        } else {
          
            const { error } = await stripe.confirmSetup({
                elements,
                confirmParams: {
                    return_url: `${window.location.origin}/dashboard?subscription_status=trialing`,
                },
            });

            if (error) {
                setMessage(error.message);
                setIsLoading(false);
            }
        }
    };

    return (
        <form onSubmit={isTrial ? handleSetup : handleSubmit}>
            <Box sx={{ mb: 3 }}>
                <PaymentElement 
                    id="payment-element" 
                    options={{
                        defaultValues: {
                            billingDetails: {
                                address: {
                                    country: 'US',
                                },
                            },
                        },
                        wallets: {
                            applePay: 'never',
                            googlePay: 'never',
                        },
                    }}
                />
            </Box>
            
            {message && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>{message}</Alert>}
            
            <Button
                disabled={isLoading || !stripe || !elements}
                type="submit"
                variant="contained"
                fullWidth
                size="large"
                sx={{ 
                    py: 2,
                    fontSize: '1.1rem',
                    bgcolor: THEME_COLORS.primary,
                    borderRadius: '8px',
                    textTransform: 'none',
                    fontWeight: 700,
                    boxShadow: '0 4px 12px rgba(17, 34, 77, 0.2)',
                    '&:hover': { 
                        bgcolor: THEME_COLORS.secondary,
                        boxShadow: '0 6px 16px rgba(17, 34, 77, 0.3)',
                    },
                    '&:disabled': { bgcolor: '#ccc' }
                }}
            >
                {isLoading ? (
                    <CircularProgress size={26} sx={{ color: 'white' }} />
                ) : (
                    isTrial ? `Start My ${trialDays}-Day Free Trial` : "Activate Dashboard"
                )}
            </Button>

            <Stack direction="row" spacing={1} justifyContent="center" alignItems="center" sx={{ mt: 3, opacity: 0.8 }}>
                <SecurityIcon sx={{ fontSize: 18, color: '#2e7d32' }} />
                <Typography variant="caption" sx={{ color: '#444', fontWeight: 500 }}>
                    AES-256 Encryption Secured by Stripe
                </Typography>
            </Stack>
        </form>
    );
};

const PaymentPage = () => {
    const [clientSecret, setClientSecret] = useState("");
    const [isTrial, setIsTrial] = useState(false);
    const [requiresPaymentMethod, setRequiresPaymentMethod] = useState(false);
    const [priceId, setPriceId] = useState(null);
    const [planAmount, setPlanAmount] = useState(null);
    const [trialDays, setTrialDays] = useState(90);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [userRole, setUserRole] = useState('');
    const navigate = useNavigate();

    useEffect(() => {
        const createSubscription = async () => {
            try {
                const token = localStorage.getItem('jwtToken');
                const role = localStorage.getItem('role');
                
                if (!token) { 
                    navigate('/'); 
                    return; 
                }
                setUserRole(role);

                let selectedPriceId;
                if (role === 'broker') {
                    selectedPriceId = process.env.REACT_APP_STRIPE_PRICE_ID_BROKER;
                } else if (role === 'carrier') {
                    selectedPriceId = process.env.REACT_APP_STRIPE_PRICE_ID_CARRIER;
                } else if (role === 'shipper') {
                    selectedPriceId = process.env.REACT_APP_STRIPE_PRICE_ID_SHIPPER;
                } else {
                    throw new Error('Invalid user role');
                }

                if (!selectedPriceId) {
                    throw new Error(`Price ID not configured for role: ${role}`);
                }
                
                setPriceId(selectedPriceId);

                const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:5000';

                const res = await axios.post(
                    `${API_BASE}/api/stripe/create-subscription`,
                    { priceId: selectedPriceId },
                    { headers: { Authorization: `Bearer ${token}` } }
                );

                setClientSecret(res.data.clientSecret);
                setIsTrial(res.data.isTrial);
                setRequiresPaymentMethod(res.data.requiresPaymentMethod);
                setPlanAmount(res.data.amount);
                if (res.data.trialDays) setTrialDays(res.data.trialDays);
            } catch (err) {
                console.error('Payment initialization error:', err);
                setError(err.message || "Payment initialization failed. Please refresh the page.");
            } finally {
                setLoading(false);
            }
        };
        createSubscription();
    }, [navigate]);

    const appearance = {
        theme: 'stripe',
        variables: {
            colorPrimary: THEME_COLORS.primary,
            colorBackground: '#ffffff',
            colorText: '#1a1a1a',
            colorDanger: '#df1b41',
            fontFamily: 'Inter, system-ui, sans-serif',
            borderRadius: '8px',
        },
    };

    if (loading) return (
        <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100vh', gap: 2 }}>
            <CircularProgress thickness={4} size={50} sx={{ color: THEME_COLORS.primary }} />
            <Typography variant="body1" fontWeight="500">Securing payment session...</Typography>
        </Box>
    );

    const planName = PLAN_NAMES[userRole] || 'ShipperDock Loadboard';

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: THEME_COLORS.background, py: { xs: 4, md: 1 } }}>
            <Container maxWidth="lg">
                <Box sx={{ mb: 1, textAlign: 'center' }}>
                    <Typography variant="h4" sx={{ fontWeight: 800, color: THEME_COLORS.primary, mb: 1 }}>
                        ShipperDock Registration
                    </Typography>
                    <Typography variant="h6" sx={{ color: 'text.secondary', fontWeight: 400 }}>
                        Securely finalize your subscription to unlock all professional features.
                    </Typography>
                </Box>

                <Box sx={{ mb: 2 }}>
                    <Stepper activeStep={1} alternativeLabel>
                        {['Account Details', 'Payment Method', 'Dashboard Access'].map((label) => (
                            <Step key={label} sx={{
                                '& .MuiStepLabel-label.Mui-active': { color: THEME_COLORS.primary, fontWeight: 'bold' },
                                '& .MuiStepIcon-root.Mui-active': { color: THEME_COLORS.warning }
                            }}>
                                <StepLabel>{label}</StepLabel>
                            </Step>
                        ))}
                    </Stepper>
                </Box>

                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 4, alignItems: 'flex-start' }}>
                  
                    <Paper elevation={0} sx={{ flex: 1, p: 4, borderRadius: 4, border: '1px solid #e0e6ed', width: '100%' }}>
                        <Typography variant="h6" sx={{ fontWeight: 700, mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
                            <CreditCardIcon color="primary" /> Order Summary
                        </Typography>
                        
                        <Stack spacing={2}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                <Typography color="text.secondary">Plan</Typography>
                               
                                <Typography fontWeight="600">{planName}</Typography>
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                <Typography color="text.secondary">Subscription Price</Typography>
                                <Typography fontWeight="600">
                                    {planAmount !== null && planAmount !== undefined ? `$${planAmount.toFixed(2)}/mo` : 'Loading...'}
                                </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                <Typography color="text.secondary">Billing Cycle</Typography>
                                <Typography fontWeight="600">Monthly</Typography>
                            </Box>
                            
                            <Divider />
                            
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Typography sx={{ fontWeight: 700 }}>Total Due Now</Typography>
                                <Typography variant="h5" sx={{ fontWeight: 800, color: THEME_COLORS.primary }}>
                                    {isTrial ? "$0.00" : (planAmount !== null && planAmount !== undefined ? `$${planAmount.toFixed(2)}` : '...')}
                                </Typography>
                            </Box>

                            {isTrial && (
                                <Box sx={{ 
                                    display: 'flex', alignItems: 'center', gap: 1.5, 
                                    bgcolor: '#f0fdf4', p: 2, borderRadius: 2, border: '1px dashed #2e7d32' 
                                }}>
                                    <CheckCircleIcon sx={{ color: '#2e7d32' }} />
                                    <Box>
                                        <Typography variant="body2" sx={{ color: '#166534', fontWeight: 700 }}>
                                            {trialDays}-Day Free Trial
                                        </Typography>
                                        <Typography variant="caption" sx={{ color: '#166534' }}>
                                            First charge occurs after trial ends. Cancel anytime.
                                        </Typography>
                                    </Box>
                                </Box>
                            )}
                        </Stack>

                        <Box sx={{ mt: 4, p: 2, bgcolor: '#fff9c4', borderRadius: 2 }}>
                            <Typography variant="caption" sx={{ color: '#5d4037', display: 'flex', gap: 1 }}>
                                <strong>Note:</strong> You can manage your subscription and download invoices anytime from settings dashboard.
                            </Typography>
                        </Box>
                    </Paper>

                    <Paper elevation={4} sx={{ flex: 1.2, p: 4, borderRadius: 4, width: '100%', position: 'relative', overflow: 'hidden' }}>
                        <Box sx={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '4px', bgcolor: THEME_COLORS.warning }} />
                        <Typography variant="h6" sx={{ fontWeight: 700, mb: 3 }}>
                            Payment Details
                        </Typography>
                        
                        {error && (
                            <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
                                {error}
                            </Alert>
                        )}
                        
                        {clientSecret && (
                            <Elements options={{ clientSecret, appearance }} stripe={stripePromise}>
                                <CheckoutForm 
                                    clientSecret={clientSecret} 
                                    isTrial={isTrial} 
                                    requiresPaymentMethod={requiresPaymentMethod}
                                    priceId={priceId}
                                    trialDays={trialDays}
                                />
                            </Elements>
                        )}
                    </Paper>
                </Box>
            </Container>
        </Box>
    );
};

export default PaymentPage;